import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import { Button } from '../../../../../../ui';
import AsyncCustomSelect from '../../../../../../ui/custom_react_select/async_select';
import { CustomSelect } from '../../../../../../ui/custom_react_select/custom_select';
import { DatePicker } from '../../../../../../ui/input/date_picker/date_picker';
import { TextInput } from '../../../../../../ui/input/text_input/text_input';
import useDebouncedJobTitleSearch from '../../../../../../utils/helpers/AsyncSelect/JobTitleGet';
import { JobTitle } from '../../../../../../utils/types/admin_types';
import {
  JobDescription,
  JobPostingTabData,
  workArrangement,
} from '../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';

export interface JobPostSettingsProps {
  jobTitleList: JobTitle[];
  jobPostingData: JobPostingTabData;
  setJobPostingData: React.Dispatch<React.SetStateAction<JobPostingTabData>>;
  setActiveTab: (value: ((prevState: string) => string) | string) => void;
  jobDescription: JobDescription;
  setJobPostId: (value: ((prevState: string) => string) | string) => void;
}

export const JobPostSettings: React.FC<JobPostSettingsProps> = ({
  jobPostingData,
  setJobPostingData,
  jobTitleList,
  setActiveTab,
  jobDescription,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const [initialData, setInitialData] =
    useState<JobPostingTabData>(jobPostingData);

  const workTypes: SelectOptions[] = [
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.hybrid), value: 'Hybrid' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.remote), value: 'Remote' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.office), value: 'Office' },
  ];

  const { loadOptionsDebounced } = useDebouncedJobTitleSearch();

  const changeDate = (timeRange: any) => {
    setJobPostingData({
      ...jobPostingData,
      startDate: timeRange.startDate,
      endDate: timeRange.endDate,
    });
  };

  const defaultJobTitles = jobTitleList.map((jobTitle) => ({
    value: jobTitle.id,
    label: jobTitle.jobTitle,
  }));

  const handleJobTitleChange = (selectedOptions: SelectOptions) => {
    setJobPostingData({ ...jobPostingData, jobTitle: selectedOptions.label });
  };

  const handleChange = (selectedOption: SelectOptions) => {
    const workType: workArrangement = selectedOption.value as workArrangement;
    setJobPostingData({ ...jobPostingData, workType: workType });
  };

  const handleSave = () => {
    setInitialData(jobPostingData);
    setIsSaveButtonDisabled(true);
    setActiveTab('jobPostDetails');
  };

  useEffect(() => {
    const isChanged =
      JSON.stringify(jobPostingData) !== JSON.stringify(initialData);
    const allFieldsSet =
      !!jobPostingData.location &&
      !!jobPostingData.startDate &&
      !!jobPostingData.endDate &&
      !!jobPostingData.jobTitle &&
      !!jobPostingData.workType &&
      !!jobPostingData.department &&
      typeof jobPostingData.active === 'boolean';

    setIsSaveButtonDisabled(!(isChanged && allFieldsSet));
  }, [jobPostingData, initialData]);

  return (
    <Box sx={{ position: 'relative', display: 'flex', minWidth: 0, flexDirection: 'column', gap: 2, p: 1.5 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          p: 2,
        }}
      >
        <Box sx={{ mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: { sm: 'space-between' } }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 800, color: 'text.primary' }}>
            {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.tabs.settings)}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={12} xl={4}>
            <DatePicker
              label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.timeRange)}
              startDate={jobPostingData.startDate}
              endDate={jobPostingData.endDate}
              onChange={changeDate}
            />
          </Grid>

          <Grid item xs={12} md={6} xl={4}>
            <Typography variant='caption' sx={{ display: 'block', py: 0.5, fontWeight: 500, color: 'text.secondary' }}>
              {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.location)}
            </Typography>
            <TextInput
              name='jobLocation'
              value={
                jobPostingData.location
                  ? (jobPostingData.location as string)
                  : (jobDescription.jobLocation as string)
              }
              type='text'
              onChange={(e) =>
                setJobPostingData({ ...jobPostingData, location: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12} md={6} xl={4}>
            <Typography variant='caption' sx={{ display: 'block', py: 0.5, fontWeight: 500, color: 'text.secondary' }}>
              {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.department)}
            </Typography>
            <TextInput
              name='jobDepartment'
              value={jobPostingData.department as string}
              type='text'
              onChange={(e) =>
                setJobPostingData({ ...jobPostingData, department: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12} md={6} xl={6}>
            <Typography variant='caption' sx={{ display: 'block', py: 0.5, fontWeight: 500, color: 'text.secondary' }}>
              {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.jobTitle)}
            </Typography>
            <AsyncCustomSelect
              defaultOptions={defaultJobTitles}
              value={
                jobPostingData.jobTitle
                  ? {
                      value: jobPostingData.jobTitle as string,
                      label: jobPostingData.jobTitle as string,
                    }
                  : undefined
              }
              onChange={handleJobTitleChange}
              loadOptions={(inputValue, callback) =>
                loadOptionsDebounced(inputValue, callback)
              }
              isMulti={false}
            />
          </Grid>

          <Grid item xs={12} md={6} xl={6}>
            <Typography variant='caption' sx={{ display: 'block', py: 0.5, fontWeight: 500, color: 'text.secondary' }}>
              {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.workingArrangement)}
            </Typography>
            <CustomSelect
              name='jobWorkType'
              value={{
                label: jobPostingData.workType as string,
                value: jobPostingData.workType as string,
              }}
              options={workTypes}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Button
            onClick={handleSave}
            variant='primary'
            label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.next)}
            disabled={isSaveButtonDisabled}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default JobPostSettings;
